// Anita Week 4 / Day 3 — Scale and Distance. U.S. Grade-3 with multiplication,
// division by 8 into equal parts, 1/4 of 64, and print-ready route strips.

const day3 = {
  id: 'day-3',
  studentId: 'anita',
  weekId: 'week-4',
  dayId: 'day-3',
  status: 'active',
  title: 'Масштаб и расстояние / Scale and Distance',
  shortTitle: 'Масштаб / Scale',
  theme:
    'Анита учится понимать простой масштаб: один сантиметр на карте — это несколько метров в жизни. / Anita learns simple scale: 1 cm on a map equals several meters in real life.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'map', 'logic', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни держит маленькую карту и линейку. Один сантиметр на карте может быть 10 метров в настоящем мире. Сегодня Анита связывает карту и жизнь через масштаб. / Nimny holds a small map and a ruler. One centimeter can be 10 real meters. Today Anita links the map to real life through scale.',
  goals: [
    'Понять масштаб «1 см = N м». / Understand scale.',
    'Умножать сантиметры на масштаб. / Multiply cm by the scale.',
    'Складывать участки маршрута и находить остаток. / Add route parts and find remainder.',
    'Делить маршрут поровну на 8 частей. / Divide a route into 8 equal parts.',
    'Работать с дробью 1/4 от целого. / Use 1/4 of the whole.',
    'Найти логическую ошибку про «маленькая карта — короткий путь». / Spot the "small map = short path" mistake.'
  ],
  vocabulary: [
    { term: 'масштаб / scale', definition: 'сколько метров в 1 см карты. / how many meters in 1 map cm.' },
    { term: 'метр / meter', definition: 'единица длины. / a length unit.' },
    { term: 'сантиметр / centimeter', definition: '1/100 метра. / one hundredth of a meter.' },
    { term: 'путь / path', definition: 'дорога от старта к цели. / road from start to goal.' },
    { term: 'длинный / long', definition: 'больший по длине. / larger in length.' },
    { term: 'короткий / short', definition: 'меньший по длине. / smaller in length.' },
    { term: 'участок / section', definition: 'часть маршрута. / a part of the route.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Задание 1 — Слова масштаба / Scale Words',
      prompt: 'Прочитай по-русски:\nмасштаб\nметр\nсантиметр\nпуть\nдлинный\nкороткий\nучасток',
      ruPrimaryText: 'Прочитай 7 слов масштаба.',
      enHelpText: 'Read 7 scale words in Russian.',
      answer: 'Анита пробует читать русские слова.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Задание 2 — Маленькое чтение / Short Reading',
      prompt:
        'Прочитай по-русски:\nМасштаб связывает карту и настоящий мир.\nНа карте путь может быть коротким, но в жизни длиннее.\nЕсли мы знаем масштаб, мы посчитаем настоящее расстояние.\nЭто помогает выбрать удобный маршрут.',
      ruPrimaryText: 'Прочитай 4 фразы про масштаб.',
      enHelpText: 'Scale links map to real life. A short line on the map can be a long real path.',
      answer: 'Анита пробует читать 4 фразы.'
    },
    {
      id: 'math-map-scale',
      type: 'text',
      title: 'Задание 3 — Масштаб карты / Map Scale',
      prompt:
        'На карте путь от дома до парка — 7 см.\n1 см на карте = 10 метров.\n\nОтветь:\n1. Сколько метров настоящий путь?',
      ruPrimaryText: 'Умножь сантиметры на масштаб 10 м.',
      enHelpText: 'Multiply centimeters by 10.',
      answer: '7 × 10 = 70 метров.'
    },
    {
      id: 'math-three-sections',
      type: 'text',
      title: 'Задание 4 — Три участка / Three Sections',
      prompt:
        'Маршрут состоит из трёх участков:\nдо дерева — 20 м;\nдо моста — 35 м;\nдо лавочки — 25 м.\n\nОтветь:\n1. Сколько метров весь маршрут?\n2. Анита прошла 50 м. Сколько осталось?',
      ruPrimaryText: 'Сумма трёх участков; потом остаток.',
      enHelpText: 'Add first, then subtract 50.',
      answer: '1) 20 + 35 + 25 = 80. 2) 80 − 50 = 30 метров.'
    },
    {
      id: 'math-divide-route',
      type: 'text',
      title: 'Задание 5 — Делим маршрут / Divide the Route',
      prompt:
        'Маршрут 72 метра разделили поровну на 8 маленьких частей.\n\nОтветь:\n1. Сколько метров в одной части?',
      ruPrimaryText: 'Деление как равные части маршрута.',
      enHelpText: 'Divide 72 by 8.',
      answer: '72 ÷ 8 = 9 метров.'
    },
    {
      id: 'math-quarter-route',
      type: 'text',
      title: 'Задание 6 — Четверть маршрута / Quarter of the Route',
      prompt:
        'Весь маршрут — 64 метра.\nАнита прошла 1/4 маршрута утром и ещё 18 метров днём.\n\nОтветь:\n1. Сколько метров в 1/4?\n2. Сколько метров прошла всего?\n3. Сколько метров осталось?',
      ruPrimaryText: 'Дробь + сложение + вычитание.',
      enHelpText: 'Find one fourth of 64 first, then add 18.',
      answer: '1) 1/4 от 64 = 16. 2) 16 + 18 = 34. 3) 64 − 34 = 30 метров.'
    },
    {
      id: 'logic-small-map',
      type: 'text',
      title: 'Задание 7 — Логика: маленькая карта / Small Map Logic',
      prompt:
        'Если карта маленькая, значит ли это, что путь в жизни тоже маленький?\nПочему нужен масштаб? Ответь 1–3 предложениями.',
      ruPrimaryText: 'Объясни, почему маленькая карта не значит короткий путь.',
      enHelpText: 'A small map can still show a long real route because of scale.',
      answer: 'Ожидаемо: «Нет. Маленькая карта показывает большой путь через масштаб.»'
    },
    {
      id: 'spelling-day3',
      type: 'text',
      title: 'Задание 8 — Правописание / Spelling',
      prompt:
        'Напиши:\nмасштаб\nметр\nсантиметр\nдлинный\nкороткий\n\nВставь буквы:\nм_сштаб\nме_р\nс_нтиметр\nдл_нный\nкоро_кий',
      ruPrimaryText: 'Напиши 5 слов масштаба и вставь буквы.',
      enHelpText: 'Write 5 scale words and fill in the missing letters.',
      answer: 'масштаб, метр, сантиметр, длинный, короткий.'
    },
    {
      id: 'creative-scale-line',
      type: 'text',
      title: 'Задание 9 — Моя линейка масштаба / My Scale Line',
      prompt:
        'Нарисуй линию 10 см в тетради или на бумаге.\nПодпиши: «1 см = 10 м».\nЧему равен весь путь линии?\n\nОтветь: путь = ___ метров.',
      ruPrimaryText: 'Нарисуй линию 10 см с подписью масштаба и посчитай метры.',
      enHelpText: 'Draw a 10-cm line labeled "1 cm = 10 m". How many meters is that?',
      answer: '10 × 10 = 100 метров.'
    },
    {
      id: 'speech-day3',
      type: 'text',
      title: 'Задание 10 — Объясни родителю / Explain to Parent',
      prompt:
        'Скажи родителю по-русски:\nЭто масштаб.\n1 см = 10 метров.\nМаленькая карта может быть большим путём.',
      ruPrimaryText: 'Объясни масштаб и связь карты с жизнью по-русски.',
      enHelpText: 'Tell the parent: this is scale, 1 cm = 10 m, a small map can be a long path.',
      answer: 'Accept if Anita explains scale in Russian with support.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Начерти полоску маршрута и таблицу участков.',
    '2. Реши задачи 3–6.',
    '3. Нарисуй линию 10 см с подписью «1 см = 10 м».',
    '4. Расскажи родителю про масштаб.'
  ],
  printWorksheet: {
    title: 'Масштаб и расстояние / Scale and Distance',
    subtitle: 'Неделя 4 · День 3 / Week 4 · Day 3',
    pages: [
      {
        title: 'Маршрут дня / Route',
        blocks: [
          { type: 'text', text: 'Имя: __________________________   Дата: __________________________' },
          { type: 'text', text: 'Миссия: Сегодня ты понимаешь простой масштаб «1 см = N м». / Today you understand scale.' },
          { type: 'text', text: 'Задание 1 — Слова масштаба. Прочитай и обведи:' },
          { type: 'list', items: [
            'масштаб / scale',
            'метр / meter',
            'сантиметр / centimeter',
            'путь / path',
            'длинный / long',
            'короткий / short',
            'участок / section'
          ] },
          { type: 'text', text: 'Задание 2 — Маленькое чтение. Поставь галочку после чтения:' },
          { type: 'list', items: [
            '□ Масштаб связывает карту и настоящий мир.',
            '□ На карте путь может быть коротким, но в жизни длиннее.',
            '□ Если мы знаем масштаб, мы посчитаем настоящее расстояние.',
            '□ Это помогает выбрать удобный маршрут.'
          ] },
          { type: 'text', text: 'Задание 3 — Масштаб карты. Готовая полоска пути:' },
          { type: 'list', items: [
            'Дом ●─────────────────────● Парк',
            'Длина на карте: 7 см',
            '1 см = 10 м',
            'Настоящий путь: 7 × 10 = ___ м'
          ] },
          { type: 'text', text: 'Задание 4 — Три участка. Готовая таблица маршрута:' },
          { type: 'list', items: [
            'Участок             | Метры',
            'Дом → дерево        |  20',
            'Дерево → мост       |  35',
            'Мост → лавочка      |  25',
            'Всего: ___',
            'Уже прошла: 50',
            'Осталось: ___'
          ] },
          { type: 'text', text: 'Задание 5 — Делим маршрут. Полоска маршрута с 8 равными секциями:' },
          { type: 'list', items: [
            '[ | | | | | | | | ]',
            'Всего 72 м',
            '72 ÷ 8 = ___ м в одной части'
          ] }
        ]
      },
      {
        title: 'Дробь и логика / Fraction and Logic',
        blocks: [
          { type: 'text', text: 'Задание 6 — Четверть маршрута. Полоска маршрута:' },
          { type: 'list', items: [
            'Весь путь = 64 м',
            '1/4 пути = ___ м',
            'Ещё прошла = 18 м',
            'Прошла всего = ___ м',
            'Осталось = ___ м'
          ] },
          { type: 'text', text: 'Задание 7 — Логика: маленькая карта. Ответь 1–3 предложения.' },
          { type: 'text', text: 'Ответ: __________________________________________________________________________________________________________________' },
          { type: 'text', text: 'Задание 8 — Правописание. Напиши и вставь буквы:' },
          { type: 'list', items: [
            'масштаб   метр   сантиметр   длинный   короткий',
            'м_сштаб    ме_р    с_нтиметр    дл_нный    коро_кий'
          ] }
        ]
      },
      {
        title: 'Моя линейка и речь / My Ruler and Speech',
        blocks: [
          { type: 'text', text: 'Задание 9 — Моя линейка масштаба. Нарисуй прямую линию 10 см (это творческий проект/рисунок) и подпиши «1 см = 10 м».' },
          { type: 'list', items: [
            '● ────────────────────── ●',
            '     10 см (1 см = 10 м)',
            'Весь путь линии = 10 × 10 = ___ метров'
          ] },
          { type: 'text', text: 'Задание 10 — Объясни родителю. Скажи:' },
          { type: 'list', items: [
            '□ Это масштаб.',
            '□ 1 см = 10 метров.',
            '□ Маленькая карта может быть большим путём.'
          ] },
          { type: 'text', text: 'Parent Check: □ прочитала слова  □ прочитала 4 фразы  □ путь на карте  □ таблица участков  □ деление 72 ÷ 8  □ 1/4 от 64  □ логика  □ линейка масштаба.' },
          { type: 'text', text: 'Notebook Mode fallback: если нет принтера, начерти полоску маршрута и линейку 10 см в тетради и повтори задания.' }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита понимает масштаб, умножает сантиметры карты на метры, работает с делением и дробью 1/4.',
    mathGoal: 'U.S. Grade 3: масштаб (× на 10), сумма участков + остаток, деление на равные части (÷ 8), 1/4 от целого.',
    russianGoal: 'Прочитать слова масштаба и 4 фразы; написать 5 слов и вставить буквы.',
    readingWritingGoal: 'Ответ 1–3 предложениями на логический вопрос про маленькую карту.',
    practicalGoal: 'Нарисовать линию 10 см с подписью масштаба и посчитать метры.',
    parentTheoryCard: {
      title: 'Как объяснить масштаб',
      purpose: 'Показать, что масштаб — это простой множитель между картой и реальностью.',
      keyMessage: 'Одна клетка или один сантиметр карты — это столько метров, сколько написано в масштабе.',
      parentScript: [
        '«1 см на карте — это 10 метров в жизни.»',
        '«Умножь сантиметры на масштаб — получишь настоящий путь.»',
        '«Если делим маршрут поровну — используем деление.»',
        '«1/4 значит одна из четырёх равных частей.»'
      ],
      practiceSteps: [
        'Возьми линейку. Отмерь 1 см и скажи: «Это 10 метров.»',
        'Отмерь 7 см. Сколько метров? 70.',
        'Возьми полоску и раздели на 8 равных частей: 72 ÷ 8 = 9.',
        'Покажи 1/4 от 64: одна из четырёх равных частей = 16.'
      ],
      doNotSay: [
        'Не решай задачи за Аниту.',
        'Не превращай масштаб в сложную формулу.',
        'Не пропускай практическую линейку 10 см.'
      ],
      paperOnlyNotes: [
        'Линейка масштаба — обычная линия с подписью.',
        'Полоску маршрута можно чертить в тетради.'
      ]
    },
    whatThisLessonTeaches: 'Масштаб как множитель, деление на равные части и работа с дробью 1/4.',
    keyMessage: 'Маленькая карта ≠ короткий путь. Масштаб решает.',
    howToExplain: 'Скажите: «Смотри на масштаб. Если 1 см = 10 м, то 7 см = 70 м.»',
    exampleDialogue:
      'Parent: 7 см × 10 = ?\nChild: 70 метров.\nParent: 72 ÷ 8 = ?\nChild: 9.\nParent: 1/4 от 64?\nChild: 16.\nParent: Маленькая карта — короткий путь?\nChild: Нет, есть масштаб.',
    readingSupport: 'Слова «сантиметр», «участок» — по слогам.',
    russianSpeechGoal: 'Анита говорит: «Это масштаб. 1 см = 10 метров.»',
    mathAnswerKey:
      'math-map-scale: 7 × 10 = 70 метров.\n' +
      'math-three-sections: 20 + 35 + 25 = 80; 80 − 50 = 30 метров.\n' +
      'math-divide-route: 72 ÷ 8 = 9 метров в одной части.\n' +
      'math-quarter-route: 1/4 от 64 = 16; 16 + 18 = 34; 64 − 34 = 30 метров.',
    spellingAnswerKey: 'масштаб\nметр\nсантиметр\nдлинный\nкороткий',
    answerKey: [
      { taskId: 'reading-words', answer: 'Попытка русского чтения 7 слов.' },
      { taskId: 'reading-sentences', answer: 'Попытка русского чтения 4 фраз.' },
      { taskId: 'math-map-scale', answer: '70 метров.' },
      { taskId: 'math-three-sections', answer: 'всего 80 м; осталось 30 м.' },
      { taskId: 'math-divide-route', answer: '9 метров.' },
      { taskId: 'math-quarter-route', answer: '16; 34; осталось 30 метров.' },
      { taskId: 'logic-small-map', answer: 'Маленькая карта не значит короткий путь. Есть масштаб.' },
      { taskId: 'spelling-day3', answer: 'масштаб, метр, сантиметр, длинный, короткий.' },
      { taskId: 'creative-scale-line', answer: '10 × 10 = 100 метров.' },
      { taskId: 'speech-day3', answer: 'Масштаб и связь карты с жизнью по-русски.' }
    ],
    check: [
      'Анита посчитала настоящий путь через масштаб.',
      'Анита нашла сумму трёх участков и остаток.',
      'Анита разделила 72 на 8 равных частей.',
      'Анита нашла 1/4 от 64.',
      'Анита объяснила, почему маленькая карта ≠ короткий путь.',
      'Анита нарисовала линейку масштаба.'
    ],
    spellingCheck: 'Проверить масштаб, метр, сантиметр, длинный, короткий.',
    readingCheck: 'Главное — попытка чтения.',
    practicalCheck: 'Линейка масштаба — просто линия с подписью.',
    drawingCriteria: 'Линия 10 см и подпись «1 см = 10 м».',
    commonMistakes: [
      '«Масштаб — это как в игре.» Правильно: «Масштаб = множитель между картой и реальностью.»',
      '«Малая карта = малый путь.» Правильно: «Смотри на масштаб.»',
      '«72 ÷ 8 = 6.» Правильно: 72 ÷ 8 = 9.'
    ],
    discussionPrompts: [
      'Почему на карте города улицы такие маленькие?',
      'Как проверить, что 1/4 = 16 от 64?',
      'Что удобнее: 8 равных частей или 4 больших?'
    ],
    ifEasy: 'Добавить 5 см × 10 м = ? и 12 см × 10 м = ?',
    ifDifficult: 'Оставить только 3 и 5 задание; заменить деление на «сколько раз 8 помещается в 72».',
    rewardGuidance: 'Базовая награда — 3 нима. Бонус за точный масштаб и объяснение.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.',
    parentOnlyNotes: 'Не пропускайте практическую линейку 10 см — это ключевой момент дня.',
    safetyNotes: 'Только выдуманные карты. Не рисуйте настоящие улицы и адреса.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'clear-scale-line', label: 'Бонус за точную линейку масштаба', amount: 1 },
      { id: 'clear-scale-explain', label: 'Бонус за объяснение масштаба', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'math-map-scale',
      'math-three-sections',
      'math-divide-route',
      'math-quarter-route',
      'logic-small-map',
      'spelling-day3',
      'creative-scale-line',
      'speech-day3'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-4-day-4'
};

export default day3;
